import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  getActiveTeam,
  getAvailableTeam,
  addDeveloper,
  removeMember,
} from "../../AdminServices/AdminProjectService";
import { getImageUrl } from "../../../Utils/urlHelper";
import { getRoleTypes } from "../../AdminServices/AdminMetadataService";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import {
  Users,
  ShieldCheck,
  FolderKanban,
  Code,
  Rocket,
  FlaskConical,
  MessageSquare,
  PauseCircle,
  Server,
  Database,
  Cpu,
  Layers,
  UserCog,
  GitBranch,
} from "lucide-react";
import NotificationAlert from "../../../Alerts/NotificationAlert";
import { useNavigate } from "react-router-dom";

const AdminAddDeveloper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectFromState = location.state?.project;
  const { projectUID } = useParams();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [project, setProject] = useState(projectFromState || {});
  const [roleTypes, setRoleTypes] = useState([]);
  const [alert, setAlert] = useState(null);
  const [unassignedTeam, setUnassignedTeam] = useState([]);

  // store selected role dropdown per developer
  const [selectedRoles, setSelectedRoles] = useState({});

  useEffect(() => {
    loadTeam();
    loadRoleTypes();
    loadAvailableTeam();
  }, [projectUID]);

  const loadTeam = async () => {
    try {
      const response = await getActiveTeam(projectUID);
      setTeam(response.data.teamMemebers || []);
    } catch (err) {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || "Failed to load Project team."
      })
      console.error("Error loading project team:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTeam = async () => {
    try {
      const response = await getAvailableTeam(projectUID);
      setUnassignedTeam(response.data.avaliableMembers || []);
    } catch (err) {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || "Failed to load available team."
      })
      console.error("Error loading available team:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadRoleTypes = async () => {
    try {
      const res = await getRoleTypes();
      if (res.data.status === "Success") {
        setRoleTypes(res.data.data);
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: "failed to load roles meta data."
      })
      console.error("Error loading roles", err);
    }
  };

  const handleRoleChange = (userUID, roleId) => {
    setSelectedRoles((prev) => ({
      ...prev,
      [userUID]: roleId,
    }));
  };

  const handleAssign = async (dev) => {
    const roleId = selectedRoles[dev.userUID];

    if (!roleId) {
      setAlert({
        type: "warning",
        message: "Please select a role!",
      });

      return;
    }

    const payload = {
      projectUID,
      userUID: dev.userUID,
      roleId,
    };

    try {
      const res = await addDeveloper(payload);

      if (res.data.status === "Success") {
        setAlert({
          type: "success",
          message: res.data.message || "Developer Assigned Successfully",
        });

        // Refresh both lists
        loadTeam();
        loadAvailableTeam();
      } else {
        setAlert({
          type: "error",
          message: res.data.message || "Assignment Failed!",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Error Assigning developer",
      });
    }
  };

  const handleRemove = async (member) => {
    const payload = {
      projectUID,
      userUID: member.userUID,
    };

    try {
      const result = await removeMember(payload);
      if (result.data.status === "Success") {
        setAlert({ type: "success", message: result.data.message });

        setSelectedRoles((prev) => {
          const updated = { ...prev };
          delete updated[member.userUID];
          return updated;
        });

        loadTeam();
        loadAvailableTeam();
      } else {
        setAlert({ type: "error", message: result.data.message });
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Error removing developer.",
      });
    }
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();

    switch (s) {
      case "development":
        return {
          color: "bg-blue-100 text-blue-700",
          icon: <Code size={12} />,
        };

      case "uat":
        return {
          color: "bg-yellow-100 text-yellow-700",
          icon: <FlaskConical size={12} />,
        };

      case "production":
        return {
          color: "bg-green-100 text-green-700",
          icon: <Rocket size={12} />,
        };

      case "discussion":
        return {
          color: "bg-purple-100 text-purple-700",
          icon: <MessageSquare size={12} />,
        };

      case "hold":
        return {
          color: "bg-red-100 text-red-700",
          icon: <PauseCircle size={12} />,
        };

      default:
        return {
          color: "bg-gray-100 text-gray-600",
          icon: null,
        };
    }
  };
  if (loading)
    return (
      <div className="flex justify-center pt-20">
        <p className="text-lg font-semibold text-[#0061A4] animate-pulse">
          Loading team...
        </p>
      </div>
    );

  return (
    <div className="min-h-screen w-full overflow-x-hidden mt-6">
      {/* HEADER */}
      {/* <div
        className="relative rounded-lg overflow-hidden shadow-md text-white mb-10"
        style={{
          backgroundImage: `url(${frameBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-8 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold drop-shadow">
              Project Team : {project?.projectName || "Project"}
            </h2>
          </div>
        </div>
      </div> */}

      <div className="relative rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/40 via-white to-cyan-50/40 pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-full h-[5px] bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-400"></div>
        <div className="relative px-6 py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* LEFT */}
            <div className="flex items-center gap-4">
              <div className="w-24 h-14 rounded-lg bg-white border flex items-center justify-center shadow-sm">
                {project.logoURL ? (
                  <img
                    src={getImageUrl(project.logoURL)}
                    alt="logo"
                    className="w-20 h-10 object-contain"
                  />
                ) : (
                  <FolderKanban className="text-blue-500" size={22} />
                )}
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {project?.projectName || "Project"}
                </h2>

                <p className="text-sm text-gray-500 mt-1 max-w-md">
                  {project?.projectDescription || "No description available"}
                </p>
              </div>
            </div>

            {/* STATUS BADGE */}
            {(() => {
              const badge = getStatusBadge(project.currentStatus);
              return (
                <div
                  className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full font-medium shadow-sm ${badge.color}`}
                >
                  {badge.icon}
                  {project.currentStatus || "N/A"}
                </div>
              );
            })()}
          </div>

          {/* DIVIDER */}
          <div className="mt-4 border-t border-gray-100"></div>

          {/* DETAILS */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-4 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <Layers size={14} className="text-gray-400" />
              <div>
                <p className="text-gray-400">Type</p>
                <p className="font-medium text-gray-800">
                  {project?.projectType || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <Cpu size={14} className="text-gray-400" />
              <div className="min-w-0 w-full">
                {" "}
                <p className="text-gray-400">Tech</p>
                <p className="font-medium text-gray-800 break-words">
                  {" "}
                  {project?.techStack || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <Server size={14} className="text-gray-400" />
              <div>
                <p className="text-gray-400">Server</p>
                <p className="font-medium text-gray-800">
                  {project?.ipAddress || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <Database size={14} className="text-gray-400" />
              <div>
                <p className="text-gray-400">DB</p>
                <p className="font-medium text-gray-800">
                  {project?.dbDetails || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <Users size={14} className="text-gray-400" />
              <div>
                <p className="text-gray-400">Team</p>
                <p className="font-medium text-gray-800">
                  {project?.teamSize || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <GitBranch size={14} className="text-gray-400" />

              <div className="min-w-0">
                <p className="text-gray-400">Repo</p>

                <div className="font-medium text-gray-800 flex flex-col gap-1 max-w-[140px]">
                  {project?.gitRepoMethod
                    ? project.gitRepoMethod.split(",").map((repo, i) => {
                        const cleanRepo = repo.trim();

                        return (
                          <a
                            key={i}
                            href={
                              cleanRepo.startsWith("http")
                                ? cleanRepo
                                : `https://${cleanRepo}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline block truncate"
                            title={cleanRepo}
                          >
                            {cleanRepo.split("/").filter(Boolean).pop()}
                          </a>
                        );
                      })
                    : "N/A"}
                </div>
              </div>
            </div>

            {/* FULL WIDTH MANAGERS */}
            <div className="col-span-2 md:col-span-6 flex items-start gap-2">
              <UserCog size={14} className="text-gray-400 mt-1" />

              <div>
                <p className="text-gray-400 text-xs">Project Managers</p>

                <div className="flex flex-wrap gap-2 mt-1">
                  {project?.projectMangers?.split(",").map((name, i) => (
                    <span
                      key={i}
                      className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-[11px] font-medium"
                    >
                      {name.trim()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TEAM SPLIT LAYOUT */}
      <div className="px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT — Unassigned Developers */}
          <div className="bg-[#f6fafe] border border-[#C7E2FF] rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-[#0061A4] mb-4 flex items-center gap-2">
              <Users size={20} /> Available Team
            </h3>

            <div className="space-y-3">
              {unassignedTeam.map((dev) => (
                <div
                  key={dev.userUID}
                  className="bg-white p-3 rounded-lg border border-[#C7E2FF] shadow-sm flex justify-between items-center"
                >
                  <span className="font-medium text-[#0061A4]">{dev.name}</span>

                  <div className="flex items-center gap-2 min-w-0">
                    <select
                      className="border rounded-md text-sm px-2 py-1 border-[#C7E2FF] text-[#0061A4]"
                      value={selectedRoles[dev.userUID] || ""}
                      onChange={(e) =>
                        handleRoleChange(dev.userUID, e.target.value)
                      }
                    >
                      <option value="">Select Role</option>
                      {roleTypes.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>

                    <button
                      className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white px-3 py-1 rounded-md text-sm shadow"
                      onClick={() => handleAssign(dev)}
                    >
                      Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Assigned Developers */}
          <div className="bg-[#f6fafe] border border-[#C7E2FF] rounded-xl p-6 shadow-sm">
            <h3 className="text-xl font-semibold text-[#0061A4] mb-4 flex items-center gap-2">
              <Users size={20} /> Assigned Team
            </h3>

            {team.length === 0 ? (
              <p className="text-gray-600 text-lg mt-4">
                No developers assigned yet.
              </p>
            ) : (
              <div className="space-y-3">
                {team.map((member) => (
                  <div
                    key={member.userUID}
                    className="flex justify-between items-center bg-white p-3 rounded-lg shadow-sm border border-[#C7E2FF]"
                  >
                    <span className="font-medium text-[#0061A4]">
                      {member.name}
                    </span>

                    <div className="flex justify-end">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-[#0061A4] border border-[#C7E2FF] flex items-center gap-1">
                        <ShieldCheck size={12} /> {member.role}
                      </span>
                      <button
                        className="bg-[#003a63] text-white px-3 py-1 rounded-md text-sm shadow ml-1"
                        onClick={() => handleRemove(member)}
                      >
                        Un-assign
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default AdminAddDeveloper;
